import React, { useState, useRef, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, Button, Box, Typography,
    TextField, Alert, Slide, Stepper, Step, StepLabel, Fade,
    IconButton, InputAdornment, Zoom, CircularProgress
} from '@mui/material';
import {
    ArrowBack, CheckCircle, Lock, Person, Badge, VpnKey,
    Visibility, VisibilityOff, WhatsApp, Security
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = 'https://tbl.zerdegroup.kz';

// Шаги
const STEPS = [
    { step: 1, title: 'Логин', icon: <Person /> },
    { step: 2, title: 'Способ', icon: <Security /> },
    { step: 3, title: 'Подтверждение', icon: <Badge /> },
    { step: 4, title: 'Пароль', icon: <VpnKey /> },
    { step: 5, title: 'Готово', icon: <CheckCircle /> }
];

// Стили TextField для тёмной темы
const inputStyles = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
        background: '#1a1a1a',
        borderRadius: '12px',
        color: '#fff',
        '&:hover fieldset': { borderColor: '#6366f1' },
        '&.Mui-focused fieldset': { borderColor: '#6366f1', borderWidth: 2 },
        '& fieldset': { borderColor: '#374151' }
    },
    '& .MuiInputLabel-root': {
        color: '#9ca3af',
        '&.Mui-focused': { color: '#6366f1' }
    }
};

interface Props {
    open: boolean;
    onClose: () => void;
    onComplete?: (login: string) => void;
}

type RecoveryMethod = 'whatsapp' | 'data';

const PasswordResetDialogWithWA: React.FC<Props> = ({ open, onClose, onComplete }) => {
    // Основные состояния
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Данные формы
    const [login, setLogin] = useState('');
    const [hasPhone, setHasPhone] = useState(false);
    const [maskedPhone, setMaskedPhone] = useState('');
    const [recoveryMethod, setRecoveryMethod] = useState<RecoveryMethod>('data');

    // WhatsApp код
    const [waCode, setWaCode] = useState(['', '', '', '', '', '']);
    const [codeSent, setCodeSent] = useState(false);
    const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Данные для метода "data"
    const [iin, setIin] = useState('');
    const [fullName, setFullName] = useState('');

    // Новый пароль
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // Сброс при закрытии
    const handleClose = () => {
        setStep(1);
        setLogin('');
        setError(null);
        setHasPhone(false);
        setMaskedPhone('');
        setRecoveryMethod('data');
        setWaCode(['', '', '', '', '', '']);
        setCodeSent(false);
        setIin('');
        setFullName('');
        setNewPassword('');
        setConfirmPassword('');
        onClose();
    };

    // Шаг 1: Проверка логина
    const checkUsername = async () => {
        if (!login.trim()) {
            setError('Введите логин');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${API_URL}/api/password-reset/check-username`, { username: login });

            if (response.data.success) {
                setHasPhone(response.data.hasPhone || false);
                setMaskedPhone(response.data.maskedPhone || '');
                setStep(2);
            } else {
                setError(response.data.message || 'Пользователь не найден');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Ошибка проверки');
        } finally {
            setLoading(false);
        }
    };

    // Шаг 2 → Шаг 3 (для метода data)
    const proceedToVerification = () => {
        setError(null);
        setStep(3);
    };

    // При выборе WhatsApp - сразу отправляем код
    const selectWhatsAppAndSend = async () => {
        if (!hasPhone) return;
        setRecoveryMethod('whatsapp');
        setStep(3);
        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${API_URL}/api/password-reset/send-whatsapp-code`, { username: login });
            if (response.data.success) {
                setCodeSent(true);
            } else {
                setError(response.data.message || 'Ошибка отправки кода');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Ошибка отправки кода');
        } finally {
            setLoading(false);
        }
    };

    // Отправка WhatsApp кода
    const sendWhatsAppCode = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${API_URL}/api/password-reset/send-whatsapp-code`, { username: login });

            if (response.data.success) {
                setCodeSent(true);
            } else {
                setError(response.data.message || 'Ошибка отправки кода');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Ошибка отправки кода. Убедитесь, что WhatsApp бот активен.');
        } finally {
            setLoading(false);
        }
    };

    // Проверка WhatsApp кода
    const verifyWhatsAppCode = async () => {
        const code = waCode.join('');
        if (code.length !== 6) {
            setError('Введите 6-значный код');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${API_URL}/api/password-reset/verify-whatsapp-code`, {
                username: login,
                code
            });

            if (response.data.success) {
                setStep(4);
            } else {
                setError(response.data.message || 'Неверный код');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Ошибка проверки кода');
        } finally {
            setLoading(false);
        }
    };

    // Проверка данных (ИИН + ФИО)
    const verifyUserData = async () => {
        if (!iin.trim() || !fullName.trim()) {
            setError('Заполните все поля');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${API_URL}/api/password-reset/verify-user-data`, {
                username: login,
                iin,
                fullName
            });

            if (response.data.success) {
                setStep(4);
            } else {
                setError(response.data.message || 'Данные не совпадают');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Ошибка проверки данных');
        } finally {
            setLoading(false);
        }
    };

    // Сброс пароля
    const resetPassword = async () => {
        if (newPassword.length < 6) {
            setError('Пароль должен быть минимум 6 символов');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Пароли не совпадают');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${API_URL}/api/password-reset/reset-password`, {
                username: login,
                newPassword
            });

            if (response.data.success) {
                setStep(5);
            } else {
                setError(response.data.message || 'Ошибка сброса пароля');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Ошибка сброса пароля');
        } finally {
            setLoading(false);
        }
    };

    // Обработка ввода кода
    const handleCodeChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;

        const newCode = [...waCode];
        newCode[index] = value.slice(-1);
        setWaCode(newCode);

        if (value && index < 5) {
            codeInputRefs.current[index + 1]?.focus();
        }
    };

    const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !waCode[index] && index > 0) {
            codeInputRefs.current[index - 1]?.focus();
        }
    };

    const handleCodePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        const newCode = [...waCode];
        text.split('').forEach((d, i) => { if (i < 6) newCode[i] = d; });
        setWaCode(newCode);
    };

    // Рендер контента
    const renderContent = () => {
        // Шаг 1: Логин
        if (step === 1) {
            return (
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 3, color: '#fff' }}>
                        Введите ваш логин
                    </Typography>
                    <TextField
                        label="Логин"
                        fullWidth
                        value={login}
                        onChange={(e) => setLogin(e.target.value)}
                        disabled={loading}
                        autoFocus
                        sx={inputStyles}
                        onKeyDown={(e) => e.key === 'Enter' && checkUsername()}
                    />
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </Box>
            );
        }

        // Шаг 2: Выбор способа
        if (step === 2) {
            return (
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 3, color: '#fff' }}>
                        Выберите способ подтверждения
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        {/* WhatsApp */}
                        <Box
                            onClick={selectWhatsAppAndSend}
                            sx={{
                                flex: 1,
                                p: 2,
                                borderRadius: 2,
                                border: `2px solid ${recoveryMethod === 'whatsapp' ? '#25D366' : '#374151'}`,
                                background: recoveryMethod === 'whatsapp' ? 'rgba(37, 211, 102, 0.1)' : 'transparent',
                                cursor: hasPhone ? 'pointer' : 'not-allowed',
                                opacity: hasPhone ? 1 : 0.5,
                                textAlign: 'center',
                                transition: 'all 0.2s',
                                '&:hover': hasPhone ? { borderColor: '#25D366' } : {}
                            }}
                        >
                            <WhatsApp sx={{ fontSize: 36, color: '#25D366', mb: 1 }} />
                            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                                WhatsApp
                            </Typography>
                            {hasPhone ? (
                                <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                                    {maskedPhone}
                                </Typography>
                            ) : (
                                <Typography variant="caption" sx={{ color: '#ef4444' }}>
                                    Телефон не указан
                                </Typography>
                            )}
                        </Box>

                        {/* Данные */}
                        <Box
                            onClick={() => setRecoveryMethod('data')}
                            sx={{
                                flex: 1,
                                p: 2,
                                borderRadius: 2,
                                border: `2px solid ${recoveryMethod === 'data' ? '#6366f1' : '#374151'}`,
                                background: recoveryMethod === 'data' ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                                cursor: 'pointer',
                                textAlign: 'center',
                                transition: 'all 0.2s',
                                '&:hover': { borderColor: '#6366f1' }
                            }}
                        >
                            <Badge sx={{ fontSize: 36, color: '#6366f1', mb: 1 }} />
                            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                                ИИН + ФИО
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                                Ввести данные
                            </Typography>
                        </Box>
                    </Box>

                    <Alert
                        severity="info"
                        sx={{
                            bgcolor: 'rgba(99, 102, 241, 0.1)',
                            color: '#a5b4fc',
                            border: '1px solid rgba(99, 102, 241, 0.3)'
                        }}
                    >
                        {recoveryMethod === 'whatsapp'
                            ? 'Код будет отправлен на привязанный номер'
                            : 'Введите ИИН и ФИО из учётной записи'
                        }
                    </Alert>
                </Box>
            );
        }

        // Шаг 3: Подтверждение
        if (step === 3) {
            // WhatsApp
            if (recoveryMethod === 'whatsapp') {
                return (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ textAlign: 'center', mb: 2, color: '#fff' }}>
                            Подтверждение через WhatsApp
                        </Typography>

                        {!codeSent ? (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <CircularProgress sx={{ color: '#25D366' }} />
                                <Typography sx={{ mt: 2, color: '#9ca3af' }}>
                                    Отправка кода на {maskedPhone}...
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Alert
                                    severity="success"
                                    sx={{ mb: 3, bgcolor: 'rgba(34, 197, 94, 0.1)', color: '#86efac' }}
                                >
                                    Код отправлен на {maskedPhone}
                                </Alert>

                                <Typography variant="body2" sx={{ color: '#9ca3af', mb: 1, textAlign: 'center' }}>
                                    Введите 6-значный код
                                </Typography>

                                <Box
                                    sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 3 }}
                                    onPaste={handleCodePaste}
                                >
                                    {waCode.map((digit, idx) => (
                                        <TextField
                                            key={idx}
                                            inputRef={(el) => codeInputRefs.current[idx] = el}
                                            size="small"
                                            value={digit}
                                            onChange={(e) => handleCodeChange(idx, e.target.value)}
                                            onKeyDown={(e) => handleCodeKeyDown(idx, e)}
                                            inputProps={{
                                                maxLength: 1,
                                                style: { textAlign: 'center', fontSize: 22, fontWeight: 'bold', padding: '10px 0' }
                                            }}
                                            sx={{
                                                width: 45,
                                                '& .MuiOutlinedInput-root': {
                                                    background: '#1a1a1a',
                                                    borderRadius: 2,
                                                    color: '#fff',
                                                    '& fieldset': { borderColor: '#374151' },
                                                    '&.Mui-focused fieldset': { borderColor: '#25D366' }
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>

                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={verifyWhatsAppCode}
                                    disabled={loading || waCode.join('').length !== 6}
                                    sx={{
                                        py: 1.5,
                                        background: '#22c55e',
                                        '&:hover': { background: '#16a34a' }
                                    }}
                                >
                                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Подтвердить'}
                                </Button>

                                <Button
                                    variant="text"
                                    fullWidth
                                    onClick={sendWhatsAppCode}
                                    disabled={loading}
                                    sx={{ mt: 1, color: '#6b7280' }}
                                >
                                    Отправить повторно
                                </Button>
                            </>
                        )}

                        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    </Box>
                );
            }

            // Метод "data"
            return (
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 2, color: '#fff' }}>
                        Подтверждение данных
                    </Typography>
                    <Alert
                        severity="info"
                        sx={{ mb: 3, bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#a5b4fc' }}
                    >
                        Данные должны совпадать с учётной записью
                    </Alert>
                    <TextField
                        label="ИИН"
                        fullWidth
                        value={iin}
                        onChange={(e) => setIin(e.target.value)}
                        disabled={loading}
                        inputProps={{ maxLength: 12 }}
                        sx={inputStyles}
                    />
                    <TextField
                        label="ФИО"
                        fullWidth
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        disabled={loading}
                        sx={inputStyles}
                    />
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </Box>
            );
        }

        // Шаг 4: Новый пароль
        if (step === 4) {
            return (
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 3, color: '#fff' }}>
                        Создайте новый пароль
                    </Typography>
                    <TextField
                        label="Новый пароль"
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={loading}
                        sx={inputStyles}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} sx={{ color: '#9ca3af' }}>
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        label="Подтвердите пароль"
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={loading}
                        sx={inputStyles}
                    />
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </Box>
            );
        }

        // Шаг 5: Готово
        if (step === 5) {
            return (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Zoom in style={{ transitionDelay: '200ms' }}>
                        <CheckCircle sx={{ fontSize: 60, color: '#22c55e', mb: 2 }} />
                    </Zoom>
                    <Typography variant="h5" sx={{ color: '#22c55e', fontWeight: 600, mb: 1 }}>
                        Пароль изменён!
                    </Typography>
                    <Typography sx={{ color: '#9ca3af', mb: 3 }}>
                        Теперь вы можете войти с новым паролем
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => {
                            onComplete?.(login);
                            handleClose();
                        }}
                        sx={{
                            py: 1.5,
                            px: 4,
                            borderRadius: '12px',
                            background: '#f3f4f6',
                            color: '#000',
                            fontWeight: 600,
                            '&:hover': { background: '#fff' }
                        }}
                    >
                        Продолжить
                    </Button>
                </Box>
            );
        }

        return null;
    };

    // Кнопка "Далее"
    const handleNext = () => {
        switch (step) {
            case 1: checkUsername(); break;
            case 2: proceedToVerification(); break;
            case 3: if (recoveryMethod === 'data') verifyUserData(); break;
            case 4: resetPassword(); break;
        }
    };

    const canProceed = () => {
        switch (step) {
            case 1: return login.trim().length > 0;
            case 2: return true;
            case 3: return recoveryMethod === 'data' ? (iin.trim() && fullName.trim()) : false;
            case 4: return newPassword.length >= 6 && newPassword === confirmPassword;
            default: return false;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            TransitionComponent={Slide}
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: '20px',
                    background: '#000',
                    border: '1px solid #374151',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                }
            }}
        >
            <DialogTitle sx={{
                textAlign: 'center',
                background: '#111',
                color: 'white',
                py: 2.5,
                borderBottom: '1px solid #374151',
                position: 'relative'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                    <Lock sx={{ color: '#6366f1' }} />
                    <Typography variant="h6" fontWeight="bold">Сброс пароля</Typography>
                </Box>
                {step > 1 && step < 5 && (
                    <IconButton
                        onClick={() => {
                            setError(null);
                            if (step === 3 && codeSent) setCodeSent(false);
                            setStep(step - 1);
                        }}
                        sx={{
                            position: 'absolute',
                            left: 12,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#9ca3af',
                            '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }
                        }}
                    >
                        <ArrowBack />
                    </IconButton>
                )}
            </DialogTitle>

            <DialogContent sx={{ p: 0, bgcolor: '#000' }}>
                {/* Stepper */}
                <Box sx={{ p: 2, pb: 0 }}>
                    <Stepper activeStep={step - 1} alternativeLabel sx={{
                        '& .MuiStepConnector-line': { borderColor: '#374151' },
                        '& .Mui-active .MuiStepConnector-line, & .Mui-completed .MuiStepConnector-line': {
                            borderColor: recoveryMethod === 'whatsapp' && step > 2 ? '#25D366' : '#6366f1'
                        },
                        '& .MuiStepIcon-root': { color: '#374151' },
                        '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': {
                            color: recoveryMethod === 'whatsapp' && step > 2 ? '#25D366' : '#6366f1'
                        },
                        '& .MuiStepLabel-label': { fontSize: '0.7rem', color: '#6b7280' },
                        '& .MuiStepLabel-label.Mui-active': {
                            color: recoveryMethod === 'whatsapp' && step > 2 ? '#25D366' : '#6366f1',
                            fontWeight: 600
                        }
                    }}>
                        {STEPS.map((s) => (
                            <Step key={s.step}><StepLabel>{s.title}</StepLabel></Step>
                        ))}
                    </Stepper>
                </Box>

                {/* Content */}
                <Fade in key={step}>{<Box>{renderContent()}</Box>}</Fade>

                {/* Buttons */}
                {step < 5 && !(step === 3 && recoveryMethod === 'whatsapp') && (
                    <Box sx={{ p: 3, pt: 0, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            onClick={handleClose}
                            disabled={loading}
                            sx={{ color: '#9ca3af', '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.1)' } }}
                        >
                            Отмена
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={loading || !canProceed()}
                            sx={{
                                minWidth: 100,
                                py: 1.5,
                                borderRadius: '10px',
                                background: '#f3f4f6',
                                color: '#000',
                                fontWeight: 600,
                                '&:hover': { background: '#fff' },
                                '&:disabled': { background: '#374151', color: '#6b7280' }
                            }}
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : (step === 4 ? 'Сохранить' : 'Далее')}
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PasswordResetDialogWithWA;
